import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import Dialog from 'client/components/aria/Dialog/Dialog';
import Button from 'client/components/aria/Button/Button';
import Table from 'client/components/aria/Table';
import { Cell, Column, Row, TableBody, TableHeader } from 'react-stately';
import styles from './NotificationContactDetailsModal.module.scss';

const NotificationContactDetailsModal = ({ isOpen, onClose, organisationName, contacts, contacted, showEmergencyContacts }) => {
    const intl = useIntl();
    const headingRef = useRef(null);
    const businessContacts = Array.isArray(contacts?.business) ? contacts.business : [];
    const emergencyContacts = Array.isArray(contacts?.emergency) ? contacts.emergency : [];
    const notAvailableLabel = intl.formatMessage({ id: 'viewEnquiryPage.notification.table.notAvailable' });

    const pickFirstValue = (values) => {
        for (const value of values) {
            if (typeof value === 'string' && value.trim().length) {
                return value.trim();
            }
        }
        return null;
    };

    const organisationLabel = typeof organisationName === 'string' && organisationName.trim().length
        ? organisationName.trim()
        : notAvailableLabel;

    // Normalise the contact records so we can render a compact summary table
    const sourceContacts = showEmergencyContacts ? emergencyContacts : businessContacts;
    const contactType = showEmergencyContacts ? 'emergency' : 'business';

    const contactRows = sourceContacts
        .map(contact => ({ contact, type: contactType }))
        .map(({ contact, type }) => {
            const nameValue = pickFirstValue([
                contact?.departmentName,
                contact?.name,
                contact?.contactName,
                contact?.role,
                contact?.displayName,
                organisationLabel
            ]);
            const phoneValue = pickFirstValue([
                contact?.telephoneNumber,
                contact?.phoneNumber,
                contact?.telephone,
                contact?.phone
            ]);
            const emailValue = pickFirstValue([
                contact?.emailAddress,
                contact?.email
            ]);

            if (!nameValue && !phoneValue && !emailValue) {
                return null;
            }

            return {
                type,
                name: nameValue ?? organisationLabel ?? notAvailableLabel,
                phone: phoneValue ?? notAvailableLabel,
                email: emailValue ?? notAvailableLabel,
                hasPhone: Boolean(phoneValue),
                hasEmail: Boolean(emailValue),
                telLink: phoneValue ? phoneValue.replace(/\s+/g, '') : null,
                mailto: emailValue
            };
        })
        .filter(Boolean);

    const hasContactDetails = contactRows.length > 0;
    const headingId = 'notificationContactModal.heading';

    useEffect(() => {
        if (isOpen && headingRef.current) {
            headingRef.current.focus({ preventScroll: true });
        }
    }, [isOpen]);

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            isDismissable
            aria-labelledby={headingId}
            data-testid="notificationContactModal"
            role="dialog"
        >
            <section className={styles.modal} role="document">
                <header className={styles.header}>
                    <div className={styles.headingGroup}>
                        <span className={styles.headingLabel} ref={headingRef} tabIndex={-1}>
                            <FormattedMessage id="viewEnquiryPage.notification.contactModal.heading" />
                        </span>
                    </div>
                    <Button
                        variant="transparent"
                        icon
                        elementType="button"
                        className={styles.headerCloseButton}
                        aria-label={intl.formatMessage({ id: 'viewEnquiryPage.notification.contactModal.close' })}
                        onPress={onClose}
                        data-testid="notificationContactModal.iconCloseButton"
                    >
                        <Icon path={mdiClose} size={1} color="white" />
                    </Button>
                </header>
                <div className={styles.body}>
                    <p className="govuk-body govuk-!-margin-bottom-6">
                        {(contacted === true || contacted === 'true')
                            ? (
                                <FormattedMessage
                                    id="viewEnquiryPage.notification.contactModal.bodyContacted"
                                    values={{
                                        organisation: organisationLabel,
                                        strong: chunks => <strong>{chunks}</strong>
                                    }}
                                />
                            )
                            : (
                                <FormattedMessage
                                    id="viewEnquiryPage.notification.contactModal.bodyNotContacted"
                                    values={{
                                        organisation: organisationLabel,
                                        strong: chunks => <strong>{chunks}</strong>
                                    }}
                                />
                            )}
                    </p>
                    {hasContactDetails
                        ? (
                            <Table
                                aria-label={intl.formatMessage({ id: 'viewEnquiryPage.notification.contactModal.heading' })}
                                data-testid="notificationContactModal.table"
                            >
                                <TableHeader>
                                    <Column>
                                        <FormattedMessage id="viewEnquiryPage.notification.contactModal.table.name" />
                                    </Column>
                                    <Column>
                                        <FormattedMessage id="viewEnquiryPage.notification.contactModal.table.phone" />
                                    </Column>
                                    <Column>
                                        <FormattedMessage id="viewEnquiryPage.notification.contactModal.table.email" />
                                    </Column>
                                </TableHeader>
                                <TableBody>
                                    {contactRows.map((row, index) => (
                                        <Row key={`contact-${index}`} className={styles.tableRow}>
                                            <Cell wrapText>
                                                <div className={styles.nameCell}>
                                                    <span className={styles.contactName}>{row.name}</span>
                                                </div>
                                            </Cell>
                                            <Cell>
                                                {row.hasPhone && row.telLink
                                                    ? <a href={`tel:${row.telLink}`}>{row.phone}</a>
                                                    : row.phone}
                                            </Cell>
                                            <Cell>
                                                {row.hasEmail && row.mailto
                                                    ? <a href={`mailto:${row.mailto}`}>{row.email}</a>
                                                    : row.email}
                                            </Cell>
                                        </Row>
                                    ))}
                                </TableBody>
                            </Table>
                        )
                        : (
                            <p className="govuk-body" data-testid="notificationContactModal.empty">
                                <FormattedMessage id="viewEnquiryPage.notification.contactModal.noDetails" />
                            </p>
                        )}
                </div>
                <div className={styles.footer}>
                    <Button
                        variant="transparent"
                        onPress={onClose}
                        data-testid="notificationContactModal.closeButton"
                        elementType="button"
                    >
                        {intl.formatMessage({ id: 'viewEnquiryPage.notification.contactModal.close' })}
                    </Button>
                </div>
            </section>
        </Dialog>
    );
};

NotificationContactDetailsModal.propTypes = {
    contacts: PropTypes.shape({
        business: PropTypes.arrayOf(PropTypes.object),
        emergency: PropTypes.arrayOf(PropTypes.object)
    }),
    contacted: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    organisationName: PropTypes.string,
    showEmergencyContacts: PropTypes.bool
};

NotificationContactDetailsModal.defaultProps = {
    contacts: {
        business: [],
        emergency: []
    },
    contacted: undefined,
    organisationName: '',
    isOpen: false,
    showEmergencyContacts: false
};

export default NotificationContactDetailsModal;
